import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
	DEFAULT_PROJECT_CURRENCY,
	type ProjectCurrency,
	type ProjectResponse,
	type ProposeCostBody,
	SUPPORTED_PROJECT_CURRENCIES,
} from "@zplanem/responses"
import type React from "react"
import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { ApiError, apiFetch } from "../../lib/api-client"
import { queryKeys } from "../../lib/query-keys"
import { useJournalCacheInvalidator } from "../hooks/use-journal-cache-invalidator"
import { getTranslations } from "../i18n"
import { useLocale } from "../locale-context"

const AMOUNT_RE = /^\d{1,10}(\.\d{1,2})?$/

export function ProposeCostModal({
	propertyId,
	projectId,
	onClose,
}: {
	propertyId: string
	projectId: string
	onClose: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const queryClient = useQueryClient()
	const invalidateJournal = useJournalCacheInvalidator(propertyId)
	const [amount, setAmount] = useState("")
	const [currency, setCurrency] = useState<ProjectCurrency>(
		DEFAULT_PROJECT_CURRENCY,
	)
	const [formError, setFormError] = useState<string | null>(null)

	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	const propose = useMutation({
		mutationFn: (body: ProposeCostBody) =>
			apiFetch<ProjectResponse>(
				`/api/common/properties/${propertyId}/projects/${projectId}/cost-proposals`,
				{ method: "POST", body: JSON.stringify(body) },
			),
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: queryKeys.projects(propertyId),
			})
			invalidateJournal()
			onClose()
		},
	})

	async function onSubmit(e: React.FormEvent) {
		e.preventDefault()
		const trimmed = amount.trim()
		if (!AMOUNT_RE.test(trimmed)) {
			setFormError(t.projects.cost.errors.amountInvalid)
			return
		}
		setFormError(null)
		try {
			await propose.mutateAsync({
				amount: trimmed,
				currency,
			})
		} catch (err) {
			setFormError(
				err instanceof ApiError ? err.message : t.projects.cost.errors.unknown,
			)
		}
	}

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-labelledby="propose-cost-title"
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={t.projects.cost.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-sm rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<div className="flex items-start justify-between">
					<h2
						id="propose-cost-title"
						className="text-xl font-bold text-foreground"
					>
						{t.projects.cost.proposeTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
						aria-label={t.projects.cost.cancel}
					>
						×
					</button>
				</div>
				<form onSubmit={onSubmit} className="mt-5 space-y-4" noValidate>
					<div className="flex gap-3">
						<div className="flex-1">
							<label
								htmlFor="cost-amount"
								className="block text-sm font-semibold text-foreground/80 mb-2"
							>
								{t.projects.cost.amountLabel}
							</label>
							<input
								id="cost-amount"
								type="text"
								inputMode="decimal"
								value={amount}
								onChange={(e) => setAmount(e.target.value)}
								placeholder={t.projects.cost.amountPlaceholder}
								className="w-full rounded-xl bg-muted dark:bg-muted/60 border border-border px-4 py-3 text-foreground placeholder:text-muted-foreground focus:border-ring outline-none"
							/>
						</div>
						<div className="w-24">
							<label
								htmlFor="cost-currency"
								className="block text-sm font-semibold text-foreground/80 mb-2"
							>
								{t.projects.cost.currencyLabel}
							</label>
							<select
								id="cost-currency"
								value={currency}
								onChange={(e) => setCurrency(e.target.value as ProjectCurrency)}
								className="form-select w-full rounded-xl bg-muted dark:bg-muted/60 border border-border pl-4 pr-9 py-3 text-foreground focus:border-ring outline-none"
							>
								{SUPPORTED_PROJECT_CURRENCIES.map((code) => (
									<option key={code} value={code}>
										{code}
									</option>
								))}
							</select>
						</div>
					</div>
					{formError ? (
						<div
							role="alert"
							className="rounded-xl border border-destructive/30 bg-destructive/10 text-destructive text-sm px-4 py-3"
						>
							{formError}
						</div>
					) : null}
					<div className="flex items-center justify-end gap-3 pt-1">
						<Button
							type="button"
							variant="ghost"
							onClick={onClose}
							disabled={propose.isPending}
						>
							{t.projects.cost.cancel}
						</Button>
						<Button type="submit" variant="brand" disabled={propose.isPending}>
							{t.projects.cost.submit}
						</Button>
					</div>
				</form>
			</div>
		</div>
	)
}
